
// client/src/contexts/CreditContext.jsx
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { fetchCreditBalance } from '../utils/creditApi';
import { useAuth } from './AuthContext';
import { toast } from 'react-toastify';

const DEFAULT_BALANCE = Object.freeze({
  available: 0,
  pendingDebits: 0,
  nextResetAt: null,
  updatedAt: null,
  lastTransactionAt: null,
});

const CreditContext = createContext({
  balance: DEFAULT_BALANCE,
  loading: false,
  loaded: false,
  refreshBalance: async () => {},
  setBalance: () => {},
});

export function CreditProvider({ children }) {
  const { session } = useAuth();
  const userId = session?.user?.id;
  const [balance, setBalanceState] = useState(DEFAULT_BALANCE);
  const [loading, setLoading] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [lastErrorAt, setLastErrorAt] = useState(0);

  const setBalance = useCallback((updater) => {
    setBalanceState((prev) => {
      const base = prev || DEFAULT_BALANCE;
      const nextValue = typeof updater === 'function' ? updater(base) : updater;
      if (!nextValue) return base;
      return { ...DEFAULT_BALANCE, ...nextValue };
    });
    setLoaded(true);
  }, []);

  const refreshBalance = useCallback(async () => {
    if (!userId) {
      setBalanceState(DEFAULT_BALANCE);
      setLoaded(false);
      return;
    }
    setLoading(true);
    try {
      const data = await fetchCreditBalance();
      if (data && typeof data.available === 'number') {
        setBalance(data);
      } else {
        setBalance(DEFAULT_BALANCE);
      }
    } catch (err) {
      const now = Date.now();
      if (now - lastErrorAt > 15_000) {
        const message = err?.response?.data?.error || err?.message || 'Unable to load credits balance.';
        toast.error(message);
        setLastErrorAt(now);
      }
    } finally {
      setLoading(false);
    }
  }, [userId, setBalance, lastErrorAt]);

  useEffect(() => {
    if (!userId) {
      setBalanceState(DEFAULT_BALANCE);
      setLoaded(false);
      return;
    }
    refreshBalance();
  }, [userId, refreshBalance]);

  const value = useMemo(() => ({
    balance,
    loading,
    loaded,
    refreshBalance,
    setBalance,
  }), [balance, loading, loaded, refreshBalance, setBalance]);

  return <CreditContext.Provider value={value}>{children}</CreditContext.Provider>;
}

export function useCredits() {
  return useContext(CreditContext);
}
